import { useCallback, useState } from 'react';
import Selector, { Props as SelectorProps } from '../Selector/Selector';
import styles from './TableSelector.module.scss';

export type TableSelectorType = {
  label: string,
  value: string,
  __isNew__?: boolean
} | null;

type Props<T extends TableSelectorType, IsMulti extends boolean> = SelectorProps<T, IsMulti> & {
  warningMessage?: string,
  readOnly?: boolean,
  fieldClassName?: string,
  onFocusStylesEnabled?: boolean,
  onFocusClassName?: string,
  onInputChange?: (inputValue: string, actionMeta: any) => void,
  onMenuOpen?: () => void,
  onMenuClose?: () => void,
}

const TableSelector = <T extends TableSelectorType, IsMulti extends boolean = false>({
  options,
  value,
  onChange,
  isCreatable,
  isDisabled,
  styles: customStyles,
  isSearchable,
  mini,
  className,
  label,
  required,
  errorMessage,
  warningMessage,
  isMulti,
  isSelected,
  readOnly,
  fieldClassName,
  onFocusStylesEnabled,
  onFocusClassName,
  onInputChange,
  onMenuOpen,
  onMenuClose,
  ...props
}: Props<T, IsMulti>) => {
  const [isHovering, setIsHovering] = useState(false);
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); 
    e.preventDefault();      
  }, []);

  const isValueEmpty = !value || 
    (Array.isArray(value) && value.length === 0) || 
    (!Array.isArray(value) && (!value || (value as TableSelectorType)?.value === ''));

  if (readOnly) {
    return (
      <div className={styles.tableSelector}>
        {value ? (
          Array.isArray(value) 
            ? (value as TableSelectorType[]).map(v => v?.label).filter(Boolean).join(', ')
            : (value as TableSelectorType)?.label
        ) : ''}
      </div>
    );
  }

  const tableSelectorStyles = {
    control: (provided: any) => ({
      ...provided,
      height: '38px',
      borderRadius: '6px !important',
      padding: '0 10px',
      boxShadow: 'none !important',
      border: 'none !important',
      backgroundColor: 'var(--gray-50)',
      transition: 'all 0.3s',
      '&:hover': {
        backgroundColor: 'var(--gray-100)',
      },
      '&--is-focused': {
        backgroundColor: 'var(--gray-100)',
        boxShadow: '0 0 0 0 rgba(0, 0, 0, 0) !important',
      },
      '&--is-disabled': {
        backgroundColor: 'var(--gray-100)',
        cursor: 'not-allowed',
      },
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: 'unset !important',
    }),
    input: (provided: any) => ({
      ...provided,
      margin: 0,
      padding: 0,
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      padding: '0 !important',
      '& svg': {
        fill: 'var(--gray-700)',
        opacity: isValueEmpty && !isHovering ? 0 : 1,
      },
    }),
    clearIndicator: (provided: any) => ({
      ...provided,
      display: isValueEmpty ? 'none' : 'flex',
    }),
    menu: (provided: any) => ({
      ...provided,
      marginTop: '4px',
      borderRadius: '6px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      backgroundColor: 'white',
      zIndex: '100 !important',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      padding: '8px 12px',
      cursor: 'pointer',
      display: 'flex !important',
      alignItems: 'center !important',
      backgroundColor: state.isFocused ? 'var(--gray-100)' : 'white',
      color: state.isSelected ? 'var(--gray-900)' : 'inherit',
      '&--is-selected': {
        backgroundColor: 'var(--blue-50)',
        color: 'var(--gray-900)',
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: 'var(--gray-700)',
    }),

    ...customStyles,
  };

  return (
    <div 
      className={`${styles.tableSelector} ${className} ${mini ? 'mini' : ''} ${errorMessage ? "selector-error" : ""}`}
      style={{ opacity: isValueEmpty && !isHovering ? 0 : 1, transition: 'opacity 0.2s ease-in-out' }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={handleClick}
    >
      <Selector
        options={options}
        value={value}
        onChange={onChange}
        isSearchable={isSearchable}
        isDisabled={isDisabled}
        styles={tableSelectorStyles}
        isCreatable={isCreatable}
        mini={mini}
        className={className}
        label={label}
        required={required}
        errorMessage={errorMessage}
        isMulti={isMulti}
        isSelected={isSelected}
        placeholder=""
        {...props}
      />
    </div>
  );
};

export default TableSelector; 